import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import ProductVideo from "./ProductVideo";

const HeroBanner: React.FC = () => {
  const { t } = useTranslation();

  return (
    <section
      id="home"
      className="relative overflow-hidden bg-white text-slate-950 dark:bg-slate-950 dark:text-white"
    >
      <div className="absolute inset-x-0 top-0 h-[520px] bg-[radial-gradient(circle_at_top,rgba(88,24,69,0.16),rgba(255,255,255,0)_55%)] dark:bg-[radial-gradient(circle_at_top,rgba(244,114,182,0.18),rgba(15,23,42,0)_56%)]" />
      <div className="hs-soft-pulse absolute left-1/2 top-28 h-64 w-64 -translate-x-1/2 rounded-full border border-primary/10 bg-primary/5 blur-sm" />
      <div className="hs-float-delay absolute left-8 top-40 hidden h-24 w-24 rounded-2xl border border-emerald-200 bg-emerald-50/80 rotate-12 lg:block dark:border-emerald-400/20 dark:bg-emerald-400/10" />
      <div className="hs-float absolute right-10 top-48 hidden h-20 w-20 rounded-full border border-pink-200 bg-pink-50/80 lg:block dark:border-pink-300/20 dark:bg-pink-300/10" />

      <div className="relative mx-auto max-w-7xl px-4 pb-16 pt-20 sm:px-6 md:pb-20 md:pt-24 lg:px-8">
        <div className="mx-auto max-w-4xl text-center">
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-primary/15 bg-white/80 px-4 py-2 text-sm font-semibold text-primary shadow-sm backdrop-blur dark:border-pink-300/20 dark:bg-slate-900/80 dark:text-pink-100">
            <span className="h-2 w-2 rounded-full bg-emerald-500" />
            {t("hero.badge")}
          </div>

          <h1 className="text-4xl font-black leading-tight text-slate-950 sm:text-6xl lg:text-7xl dark:text-white">
            {t("hero.headline")}
          </h1>
          <p className="mx-auto mt-6 max-w-3xl text-lg leading-8 text-slate-600 dark:text-slate-300">
            {t("hero.subheading")}
          </p>
          <p className="mx-auto mt-4 max-w-3xl text-base font-semibold leading-7 text-primary dark:text-pink-200">
            {t("hero.ebmIntegration")}
          </p>

          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
            <Link
              to="/pricing-calculator"
              className="inline-flex items-center justify-center rounded-xl bg-primary px-7 py-3.5 text-base font-bold text-white shadow-lg shadow-primary/20 transition hover:-translate-y-0.5 hover:bg-[#6d1f55]"
            >
              {t("hero.calculatePrice")}
            </Link>
            <a
              href="#features"
              className="inline-flex items-center justify-center rounded-xl border border-slate-300 bg-white/90 px-7 py-3.5 text-base font-bold text-slate-800 shadow-sm backdrop-blur transition hover:-translate-y-0.5 hover:border-primary hover:text-primary dark:border-slate-700 dark:bg-slate-900/90 dark:text-slate-100"
            >
              {t("hero.exploreFeatures")}
            </a>
          </div>
        </div>

        <div className="relative mx-auto mt-12 max-w-6xl">
          <div className="absolute -inset-4 rounded-[2rem] bg-gradient-to-r from-primary/10 via-pink-300/10 to-emerald-300/10 blur-2xl" />
          <div className="relative rounded-[2rem] border border-slate-200 bg-white/75 p-3 shadow-2xl shadow-slate-900/15 backdrop-blur-xl dark:border-slate-800 dark:bg-slate-900/75 dark:shadow-black/30">
            <div className="mb-3 flex items-center justify-between px-3 py-2">
              <div className="flex items-center gap-2">
                <span className="h-3 w-3 rounded-full bg-red-400" />
                <span className="h-3 w-3 rounded-full bg-amber-400" />
                <span className="h-3 w-3 rounded-full bg-emerald-400" />
              </div>
              <div className="hidden rounded-full bg-slate-100 px-4 py-1 text-xs font-bold uppercase tracking-wide text-slate-500 sm:block dark:bg-slate-800 dark:text-slate-400">
                {t("hero.workspace")}
              </div>
              <div className="h-7 w-7 rounded-full bg-primary/10" />
            </div>

            <div className="relative">
              <div className="absolute -right-4 top-10 z-10 hidden rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-800 shadow-xl shadow-slate-900/10 md:block dark:border-slate-700 dark:bg-slate-900 dark:text-white">
                {t("hero.liveSales")}
                <div className="mt-1 text-xs font-medium text-emerald-600">
                  {t("hero.salesLift")}
                </div>
              </div>
              <div className="absolute -bottom-5 left-8 z-10 hidden rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-800 shadow-xl shadow-slate-900/10 md:block dark:border-slate-700 dark:bg-slate-900 dark:text-white">
                {t("hero.ebmReady")}
                <div className="mt-1 text-xs font-medium text-primary dark:text-pink-200">
                  {t("hero.receiptsSynced")}
                </div>
              </div>
              <ProductVideo className="w-full shadow-none" />
            </div>
          </div>
        </div>

        <div className="mx-auto mt-10 grid max-w-3xl grid-cols-3 gap-3 rounded-2xl border border-slate-200 bg-white/80 p-3 text-center shadow-sm backdrop-blur dark:border-slate-800 dark:bg-slate-900/80">
          {[
            [t("hero.statEbm"), t("hero.statCertified")],
            [t("hero.statMultistore"), t("hero.statReady")],
            [t("hero.statLive"), t("hero.statAnalytics")],
          ].map(([value, label]) => (
            <div
              key={value}
              className="rounded-xl bg-slate-50 px-3 py-4 dark:bg-slate-800/70"
            >
              <div className="text-xl font-black text-slate-950 dark:text-white">
                {value}
              </div>
              <div className="mt-1 text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                {label}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
          <a
            href="https://play.google.com/store/apps/details?id=ai.hova.hovastore" // Replace with actual link to Google Play
            target="_blank"
            rel="noopener noreferrer"
            className="w-[42%] transition hover:-translate-y-0.5 sm:w-auto"
          >
            <img
              src="/assets/playstore.png" // Replace with the image path for your app display
              alt="HovaStore App"
              className="h-12 w-full object-contain"
            />
          </a>
          <a
            href="https://apps.apple.com/fr/app/hovastore/id6479642676" // Replace with actual App Store link
            target="_blank"
            rel="noopener noreferrer"
            className="w-[42%] transition hover:-translate-y-0.5 sm:w-auto"
          >
            <img
              src="/assets/appstore.png" // Replace with the image path for your app display
              alt="HovaStore App"
              className="h-12 w-full object-contain"
            />
          </a>
          <a
            href="#learn-more"
            className="flex h-12 w-full items-center justify-center rounded-lg border border-slate-300 bg-white px-6 py-3 text-center font-bold text-primary shadow-sm transition hover:border-primary sm:w-auto dark:border-slate-700 dark:bg-slate-900"
          >
            {t("hero.pos")}
          </a>
        </div>
      </div>
    </section>
  );
};

export default HeroBanner;
